import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Grid,
  Card,
  Typography,
  Box,
  useMediaQuery,
  Stack,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  Container,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import { toast } from 'react-toastify';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const ARAC_RENKLERI = {
  'Küçük Araç': '#1C64F2',
  'Büyük Araç': '#16BDCA',
  Palet: '#FDBA8C',
  'Koli / Kargo': '#E74694',
  Ring: '#6366F1',
  'Askılı Araç': '#FACC15',
};

const MallStatisticsPage = () => {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFetchingRef = useRef(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  const { hasPermission: statisticsPermission, loading: permissionLoading } =
    useServicePermission(SERVICES.STATISTICS);

  useEffect(() => {
    if (permissionLoading) return;
    if (!mallID || !parsedUser?.token) {
      setLoading(false);
      return;
    }

    const fetchAllData = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);
      try {
        const userHasPermission = statisticsPermission;
        setHasPermission(userHasPermission);

        if (userHasPermission) {
          const analyticsResponse = await Services.GetLiveAnalyticsMallGeneral(
            parsedUser.token,
            mallID
          );
          if (analyticsResponse.data && analyticsResponse.data.result) {
            setAnalyticsData(analyticsResponse.data.result);
          }
        } else {
          setAnalyticsData({});
          setShowApplyBanner(true);
        }
      } catch (err) {
        console.error('Veri yüklenirken hata oluştu:', err);
        toast.error('Veriler yüklenirken bir hata oluştu.');
        setAnalyticsData({});
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    };
    fetchAllData();
  }, [mallID, parsedUser?.token, statisticsPermission, permissionLoading]);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);

  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', {
        withOffices: true,
      });
      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find(
          (item) => item.id === mallID
        );
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';
        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName:
            parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'İstatistikler',
        };
        const response = await Services.createContactForm(
          parsedUser.token,
          formData
        );
        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(
            response.data?.message || 'Talep gönderilirken bir hata oluştu.'
          );
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  const chartConfigurations = useMemo(() => {
    const data = analyticsData || {};
    const now = new Date();
    const monthIdx = now.getMonth();
    const year = now.getFullYear();
    const lastDayOfMonth = new Date(year, monthIdx + 1, 0).getDate();
    const MONTH_ABBREV_TR = [
      'Oca',
      'Şub',
      'Mar',
      'Nis',
      'May',
      'Haz',
      'Tem',
      'Ağu',
      'Eyl',
      'Eki',
      'Kas',
      'Ara',
    ];
    const currentMonthAbbrev = MONTH_ABBREV_TR[monthIdx];
    const daysInMonth = Array.from({ length: lastDayOfMonth }, (_, i) => i + 1);
    const pad2 = (n) => n.toString().padStart(2, '0');

    // --- YENİ EKLENEN GRAFİK: Aylık Kullanılan Araç Dağılımı ---
    const tools = data.monthlyTransportationTools || {};
    const toolKeys = Object.keys(tools);
    const toolCounts = toolKeys.map((k) => parseFloat(tools[k]) || 0);
    const toolTotal = toolCounts.reduce((sum, v) => sum + v, 0) || 1;

    // --- ESKİ GRAFİK (YORUMA ALINDI) ---
    /*
    const mmt = data.monthlyMalKabulTransactions || {};
    const timeRanges = Object.keys(mmt.inbound || {});
    const barSeries = [
      { name: 'İçeri Giren Paket', data: timeRanges.map(r => (mmt.inbound || {})[r] ?? 0) },
      { name: 'Mağaza - Depo Arası', data: timeRanges.map(r => (mmt.warehouse || {})[r] ?? 0) },
      { name: 'Dışarı Çıkan Paket', data: timeRanges.map(r => (mmt.outbound || {})[r] ?? 0) },
    ];
    const totalInbound = barSeries[0].data.reduce((sum, val) => sum + val, 0);
    const totalWarehouse = barSeries[1].data.reduce((sum, val) => sum + val, 0);
    const totalOutbound = barSeries[2].data.reduce((sum, val) => sum + val, 0);
    const malKabulPieSeries = [totalInbound, totalWarehouse, totalOutbound];
    const malKabulPieLabels = ['İçeri Giren Paket', 'Mağaza - Depo Arası', 'Dışarı Çıkan Paket'];
    const malKabulPieColors = ['#1C64F2', '#FDBA8C', '#16BDCA'];
    */

    const dropboxIncome = data.dropboxMonthlyIncomeReport || {};
    const checkroomIncome = data.checkroomMonthlyIncomeReport || {};
    const emanetData = daysInMonth.map(
      (day) => dropboxIncome[`${year}-${pad2(monthIdx + 1)}-${pad2(day)}`] ?? 0
    );
    const vestiyerData = daysInMonth.map(
      (day) =>
        checkroomIncome[`${year}-${pad2(monthIdx + 1)}-${pad2(day)}`] ?? 0
    );
    const lineSeries = [
      { name: 'Emanet Dolabı', data: emanetData },
      { name: 'Vestiyer', data: vestiyerData },
    ];

    const clickCollectUsage =
      parseInt(data.clickAndCollectMonthlyReport, 10) || 0;
    const petCarUsage = parseInt(data.petCarMonthlyUsageReport, 10) || 0;
    const kidCarUsage = parseInt(data.kidCarMonthlyUsageReport, 10) || 0;
    const taxFreeUsage = parseInt(data.taxFreeMonthlyUsageReport, 10) || 0;
    const homeDeliveryUsage = parseInt(data.homeDeliveryMonthlyUsageReport, 10) || 0;
    const deskSeries = [
      {
        name: 'Kullanım',
        data: [clickCollectUsage, petCarUsage, kidCarUsage, taxFreeUsage, homeDeliveryUsage],
      },
    ];

    const normalizeReport = (report) => {
      if (!report || typeof report !== 'object') return {};
      if (Array.isArray(report))
        return report.reduce(
          (acc, { day, total }) => ({ ...acc, [day]: total }),
          {}
        );
      return report;
    };
    const petIncomeMap = normalizeReport(data.petCarMonthlyIncomeReport);
    const kidIncomeMap = normalizeReport(data.kidCarMonthlyIncomeReport);
    const petData = daysInMonth.map(
      (day) =>
        parseFloat(
          petIncomeMap[`${year}-${pad2(monthIdx + 1)}-${pad2(day)}`]
        ) || 0
    );
    const kidData = daysInMonth.map(
      (day) =>
        parseFloat(
          kidIncomeMap[`${year}-${pad2(monthIdx + 1)}-${pad2(day)}`]
        ) || 0
    );
    const petSeries = [
      { name: 'Pet Arabası', data: petData },
      { name: 'Çocuk Arabası', data: kidData },
    ];

    const hf = data.handsfreeDeliveryReport || {};
    const hfTotal =
      parseInt(hf.total, 10) ||
      (parseInt(hf.hotel, 10) || 0) +
        (parseInt(hf.box, 10) || 0) +
        (parseInt(hf.address, 10) || 0) +
        (parseInt(hf.easypoint, 10) || 0);
    const handsFreeDonutSeries =
      hfTotal > 0
        ? [
            ((parseInt(hf.hotel, 10) || 0) * 100) / hfTotal,
            ((parseInt(hf.box, 10) || 0) * 100) / hfTotal,
            ((parseInt(hf.address, 10) || 0) * 100) / hfTotal,
            ((parseInt(hf.easypoint, 10) || 0) * 100) / hfTotal,
          ]
        : [0, 0, 0, 0];

    const hfIncome = data.handsfreeIncomeReport || {};
    const hfIncomeData = daysInMonth.map(
      (day) =>
        parseFloat(hfIncome[`${year}-${pad2(monthIdx + 1)}-${pad2(day)}`]) || 0
    );
    const handsFreeLineSeries = [{ name: 'Hands Free', data: hfIncomeData }];

    return [
      {
        id: 'VehicleDistribution',
        title: 'Aylık Kullanılan Araç Dağılımı',
        subtitle: 'Taşıma Aracı Adetine Göre',
        type: 'donut',
        series: toolCounts,
        options: {
          chart: { type: 'donut', toolbar: { show: false } },
          labels: toolKeys,
          colors: toolKeys.map((key) => ARAC_RENKLERI[key] || '#94A3B8'),
          plotOptions: {
            pie: {
              donut: {
                size: '65%',
                labels: {
                  show: true,
                  name: {
                    show: true,
                    fontSize: '16px',
                    fontWeight: 600,
                    offsetY: -5,
                  },
                  value: {
                    show: true,
                    fontSize: '28px',
                    fontWeight: 700,
                    formatter: (val) =>
                      `% ${((val / toolTotal) * 100).toFixed(0)}`,
                  },
                  total: { show: false },
                },
              },
            },
          },
          legend: { position: 'bottom' },
          tooltip: {
            y: {
              formatter: (val) =>
                `${val} adet (${((val / toolTotal) * 100).toFixed(1)}%)`,
            },
          },
          dataLabels: { enabled: false },
        },
      },
      /*
      {
        id: 'MalKabulPie',
        title: 'Aylık Mal Kabul Dağılımı',
        subtitle: 'Genel Toplam Adet',
        type: 'donut',
        series: malKabulPieSeries,
        options: {
          chart: { type: 'donut', toolbar: { show: false } },
          labels: malKabulPieLabels,
          colors: malKabulPieColors,
          plotOptions: {
            pie: {
              donut: {
                size: '65%',
                labels: {
                  show: true,
                  value: { show: true, fontSize: '28px', fontWeight: 700, formatter: (val) => val.toFixed(0) },
                  total: {
                    show: true,
                    showAlways: true,
                    label: 'Toplam',
                    fontSize: '16px',
                    fontWeight: 600,
                    formatter: (w) => {                      
                      const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                      return total.toFixed(0);
                    }
                  }
                }
              }
            }
          },
          legend: { position: 'bottom' },
          dataLabels: { enabled: false },
          tooltip: { y: { formatter: (val) => `${val} adet` } }
        }
      },
      */
      {
        id: 'EmanetVestiyer',
        title: 'Aylık Emanet Dolabı - Vestiyer Ciro Hareketleri',
        subtitle: 'Toplam Ciro',
        type: 'line',
        series: lineSeries,
        options: {
          chart: { toolbar: { show: false } },
          stroke: { curve: 'smooth', width: 2 },
          colors: ['#2563EB', '#14B8A6'],
          markers: { size: 4 },
          xaxis: {
            categories: daysInMonth.map(
              (day) => `${currentMonthAbbrev} ${day}`
            ),
          },
          yaxis: {
            title: { text: 'Toplam Ciro' },
            labels: { formatter: (val) => `${val.toFixed(0)} ₺` },
          },
          tooltip: { y: { formatter: (val) => `${val.toFixed(2)} ₺` } },
        },
      },
      {
        id: 'EasyPoint',
        title: 'Aylık Easy Point Desk Hareketleri',
        subtitle: 'Toplam Kullanım Adedi',
        type: 'bar',
        series: deskSeries,
        options: {
          chart: { toolbar: { show: false } },
          plotOptions: { bar: { columnWidth: '30%', borderRadius: 8 } },
          colors: ['#1C64F2'],
          xaxis: {
            categories: [
              'Click&Collect',
              'Pet Arabası',
              'Çocuk Arabası',
              'Tax Free',
              'Eve Teslim',
            ],
          },
          yaxis: {
            title: { text: 'Toplam Kullanım Adedi' },
            min: 1,
            logarithmic: true,
          },
          dataLabels: { enabled: false },
        },
      },
      {
        id: 'PetCocukAraba',
        title: 'Aylık Pet - Çocuk Arabası Hareketleri',
        subtitle: 'Toplam Ciro',
        type: 'line',
        series: petSeries,
        options: {
          chart: { toolbar: { show: false } },
          stroke: { curve: 'smooth', width: 2 },
          colors: ['#2563EB', '#14B8A6'],
          markers: { size: 4 },
          xaxis: {
            categories: daysInMonth.map(
              (day) => `${currentMonthAbbrev} ${day}`
            ),
          },
          yaxis: {
            title: { text: 'Toplam Ciro' },
            labels: { formatter: (val) => `${val.toFixed(0)} ₺` },
          },
          tooltip: { y: { formatter: (val) => `${val.toFixed(2)} ₺` } },
        },
      },
      {
        id: 'HandsFreeDonut',
        title: 'Aylık Hands Free Teslimat Dağılımı',
        subtitle: 'Teslimat Tercihine Göre Dağılımı',
        type: 'donut',
        series: handsFreeDonutSeries,
        options: {
          chart: { toolbar: { show: false } },
          labels: ['Otel', 'Emanet Dolabı', 'Eve Teslim', 'Easy Point'],
          colors: ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694'],
          plotOptions: { pie: { donut: { size: '65%' } } },
          legend: { position: 'bottom' },
          dataLabels: { enabled: false },
          tooltip: { y: { formatter: (val) => `${val.toFixed(1)}%` } },
        },
      },
      {
        id: 'HandsFreeLine',
        title: 'Aylık Hands Free Ciro Hareketleri',
        subtitle: 'Toplam Ciro',
        type: 'line',
        series: handsFreeLineSeries,
        options: {
          chart: { toolbar: { show: false } },
          stroke: { curve: 'smooth', width: 2 },
          colors: ['#2563EB'],
          markers: { size: 4 },
          xaxis: {
            categories: daysInMonth.map(
              (day) => `${currentMonthAbbrev} ${day}`
            ),
          },
          yaxis: { title: { text: 'Toplam Ciro' } },
        },
      },
    ];
  }, [analyticsData]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') return series.map(() => 0);
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map((s) => ({
        ...s,
        data: Array.isArray(s.data) ? s.data.map(() => 0) : [],
      }));
    }
    return [];
  };

  const frontAuth = parsedUser?.frontAuth || [];
  const hasMallManagement = frontAuth.some(
    (auth) => typeof auth === 'string' && auth.includes('mall-management-user')
  );
  const hasStatisticsAuth = frontAuth.some(
    (auth) => typeof auth === 'string' && auth.includes('statistics')
  );
  const isAccessDenied = hasMallManagement && !hasStatisticsAuth;

  if (loading || permissionLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAccessDenied) {
    return (
      <>
        <Helmet>
          <title>Yetkisiz Erişim</title>
        </Helmet>
        <Container>
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: '60vh', textAlign: 'center' }}
          >
            <Typography variant="h5">
              Erişim yetkiniz bulunmamaktadır.
            </Typography>
            <Typography color="text.secondary">
              Lütfen yöneticinizle iletişime geçiniz.
            </Typography>
          </Stack>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Özet</title>
      </Helmet>
      <Stack
        sx={{ px: 4, mb: 2 }}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        {showApplyBanner && !hasPermission && (
          <Stack
            sx={{
              width: '100%',
              mb: 4,
              mx: 'auto',
              borderBottom: '1px solid #D6BBFB',
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Alert
              severity="info"
              sx={{
                backgroundColor: 'white',
                alignItems: 'center',
                pr: 4,
                '& .MuiAlert-icon': { color: theme.palette.info.main },
              }}
              action={
                <Stack direction="row" gap={1} alignItems="center">
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={handleApplyClick}
                    sx={{ padding: '10px 16px', cursor: 'pointer' }}
                  >
                    Başvur
                  </Button>
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowApplyBanner(false)}
                  >
                    <Iconify icon="eva:close-fill" width={20} height={20} />
                  </IconButton>
                </Stack>
              }
              onClose={() => setShowApplyBanner(false)}
            >
              Bu hizmet şu anda aktif değil. Grafik ve liste verilerini
              görüntülemek için başvuruda bulunabilirsiniz.
            </Alert>
          </Stack>
        )}
        <Typography variant="h5">Özet</Typography>
      </Stack>

      <Grid container spacing={2} sx={{ px: 4 }}>
        {chartConfigurations.map((cfg, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Card sx={{ position: 'relative', height: 360, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {cfg.title}
              </Typography>
              <Typography
                sx={{ color: '#4B5563', fontSize: '14px' }}
                variant="p"
                gutterBottom
              >
                {cfg.subtitle}
              </Typography>
              <ReactApexChart
                options={cfg.options}
                series={hasPermission ? cfg.series : zeroOutSeries(cfg.series)}
                type={cfg.type}
                height={260}
              />
            </Card>
          </Grid>
        ))}
      </Grid>
      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize
            iletilecektir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button onClick={handleConfirmApply} autoFocus>
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MallStatisticsPage;
